/**
 * PDF Styles Configuration
 * Centralized styling for all PDF exports
 */

// Brand Colors (matching Tailwind config)
export const colors = {
  primary: '#2563eb',      // blue-600
  primaryDark: '#1d4ed8',  // blue-700
  secondary: '#64748b',    // slate-500
  success: '#16a34a',      // green-600
  warning: '#d97706',      // amber-600
  danger: '#dc2626',       // red-600
  text: '#1f2937',         // gray-800
  textLight: '#6b7280',    // gray-500
  border: '#e5e7eb',       // gray-200
  background: '#f9fafb',   // gray-50
  white: '#ffffff',
  black: '#000000',
};

// Font sizes (in points)
export const fontSizes = {
  title: 24,
  subtitle: 18,
  heading: 14,
  subheading: 12,
  body: 10,
  small: 9,
  tiny: 8,
};

// Spacing (in mm)
export const spacing = {
  margin: 15,
  padding: 10,
  lineHeight: 6,
  sectionGap: 12,
  paragraphGap: 4,
};

// Page dimensions (A4 in mm)
export const page = {
  width: 210,
  height: 297,
  marginTop: 20,
  marginBottom: 25,
  marginLeft: 15,
  marginRight: 15,
  contentWidth: 180, // 210 - 15 - 15
  headerHeight: 25,
  footerHeight: 15,
};

// Status colors for badges
export const statusColors = {
  active: { bg: '#dbeafe', text: '#1e40af' },      // blue
  completed: { bg: '#dcfce7', text: '#166534' },   // green
  pending: { bg: '#fef3c7', text: '#92400e' },     // amber
  blocked: { bg: '#fee2e2', text: '#991b1b' },     // red
  not_started: { bg: '#f3f4f6', text: '#374151' }, // gray
  in_progress: { bg: '#dbeafe', text: '#1e40af' }, // blue
};

// Priority colors
export const priorityColors = {
  high: { bg: '#fee2e2', text: '#991b1b' },
  medium: { bg: '#fef3c7', text: '#92400e' },
  low: { bg: '#dcfce7', text: '#166534' },
  normal: { bg: '#f3f4f6', text: '#374151' },
};

// Event type icons (using simple ASCII text for PDF compatibility)
// Emojis and special Unicode characters don't render properly in jsPDF
export const eventIcons = {
  action_completed: '[Done]',
  stage_started: '[Start]',
  stage_completed: '[End]',
  document_uploaded: '[Doc]',
  note_added: '[Note]',
  case_created: '[New]',
};

// Table styles for jspdf-autotable
export const tableStyles = {
  default: {
    headStyles: {
      fillColor: [37, 99, 235], // primary blue
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [31, 41, 55], // gray-800
      cellPadding: 3,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251], // gray-50
    },
    styles: {
      lineColor: [229, 231, 235], // gray-200
      lineWidth: 0.1,
    },
  },
  compact: {
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 2,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [31, 41, 55],
      cellPadding: 2,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    styles: {
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
  },
  timeline: {
    headStyles: {
      fillColor: [100, 116, 139], // slate-500
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 3,
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [31, 41, 55],
      cellPadding: 3,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    styles: {
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
  },
};

// Document types
export const documentTypes = {
  CASE_SUMMARY: 'Case Summary Report',
  CASE_TIMELINE: 'Case Timeline Report',
  STAFF_TIMELINE: 'Staff Timeline Report',
  WORKFLOW: 'Workflow Documentation',
};

// Format helpers
export const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Truncate text for PDF cells
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

// Convert hex color to RGB array for jsPDF
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
};

// Get status display text
export const getStatusDisplay = (status) => {
  const statusMap = {
    active: 'Active',
    completed: 'Completed',
    pending: 'Pending',
    blocked: 'Blocked',
    not_started: 'Not Started',
    in_progress: 'In Progress',
    closed: 'Closed',
  };
  return statusMap[status?.toLowerCase()] || status || 'Unknown';
};

// Get priority display text
export const getPriorityDisplay = (priority) => {
  const priorityMap = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    normal: 'Normal',
  };
  return priorityMap[priority?.toLowerCase()] || priority || 'Normal';
};
